
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, ChevronRight } from 'lucide-react';
import { teamService } from '@/services/teamService';
import { TeamConnection } from '@/types/chat';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';

interface TeamSelectorProps {
  userId: string;
  onTeamSelect: (team: TeamConnection) => void;
  selectedTeam: TeamConnection | null;
}

const TeamSelector = ({ userId, onTeamSelect, selectedTeam }: TeamSelectorProps) => {
  const { profile } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const [teams, setTeams] = useState<TeamConnection[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadUserTeams();
  }, [userId, profile, internalUser]);

  const loadUserTeams = async () => {
    if (!userId) return;
    
    console.log('=== LOADING USER TEAMS FOR CHAT ===');
    console.log('User ID:', userId);
    console.log('Supabase Profile:', profile);
    console.log('Internal User:', internalUser);
    
    setLoading(true);
    try {
      // Get all teams for this user using the teamService
      const userTeams = await teamService.getUserTeams(userId);
      console.log('Teams fetched from teamService:', userTeams);

      // Convert teams to TeamConnection format for chat
      const teamConnections: TeamConnection[] = userTeams.map(team => {
        // Determine connection type based on user role
        let connectionType = 'member';
        if (team.created_by === userId) {
          connectionType = 'owner';
        } else if (profile?.role === 'coach' || internalUser?.role === 'coach') {
          connectionType = 'coach';
        }

        return {
          team_id: team.team_id,
          team_name: team.team_name,
          connection_type: connectionType,
          users: [] // Will be populated when team is selected
        };
      });

      console.log('Team connections for chat:', teamConnections);
      setTeams(teamConnections);
    } catch (error) {
      console.error('Error loading user teams for chat:', error);
      setTeams([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-sports-green"></div>
        <span className="ml-2 text-gray-400">Loading teams...</span>
      </div>
    );
  }

  if (teams.length === 0) {
    const userRole = profile?.role || internalUser?.role || 'player';
    const roleMessage = userRole === 'coach' || userRole === 'team-owner'
      ? 'You need to be assigned to a team or own a team to use team chat'
      : 'You need to be a member of a team to use team chat';

    return (
      <div className="text-center py-4">
        <p className="text-gray-400 text-sm">No teams found</p>
        <p className="text-gray-500 text-xs mt-1">
          {roleMessage}
        </p>
        <Button 
          onClick={loadUserTeams}
          variant="ghost"
          size="sm"
          className="mt-2 text-sports-green hover:bg-sports-green/20"
        >
          Refresh Teams
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Select Team
        </label>
        <Select 
          value={selectedTeam?.team_id.toString() || ''} 
          onValueChange={(value) => {
            const team = teams.find(t => t.team_id.toString() === value);
            if (team) {
              console.log('Team selected for chat:', team);
              onTeamSelect(team);
            }
          }}
        >
          <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
            <SelectValue placeholder="Choose a team..." />
          </SelectTrigger>
          <SelectContent className="bg-gray-700 border-gray-600">
            {teams.map((team) => (
              <SelectItem key={team.team_id} value={team.team_id.toString()} className="text-white hover:bg-gray-600">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  <span>{team.team_name}</span>
                  <span className="text-xs text-gray-400">
                    ({team.connection_type})
                  </span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedTeam && (
        <div className="bg-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-white">{selectedTeam.team_name}</h4>
            <span className="text-xs text-sports-green bg-sports-green/20 px-2 py-1 rounded capitalize">
              {selectedTeam.connection_type}
            </span>
          </div>
          
          <p className="text-sm text-gray-400">
            Team chat ready - you can now add this team to chat groups
          </p>
        </div>
      )}
    </div>
  );
};

export default TeamSelector;
